import {
	differenceInCalendarDays,
	differenceInCalendarWeeks,
	format,
	getISOWeek,
	getISOWeekYear,
	getMonth,
	getYear,
	isSameWeek,
	startOfMonth,
	startOfWeek,
} from "date-fns";
import { cs } from "date-fns/locale/cs";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

export const WEEK_OPTIONS = { weekStartsOn: 1 } as const;
export const EARLIER_WEEK_HORIZON_DAYS = 30;

export type RecentBucket =
	| { kind: "today"; key: "today" }
	| { kind: "thisWeek"; key: "thisWeek" }
	| { kind: "earlierWeek"; key: string; representativeDate: Date }
	| { kind: "earlierMonth"; key: string; representativeDate: Date };

export type WeekLabels = {
	thisWeek: string;
	lastWeek: string;
	weeksAgo: (count: number) => string;
};

const capitalizeFirst = (value: string): string => {
	if (value.length === 0) return value;
	return value.charAt(0).toUpperCase() + value.slice(1);
};

export const formatWeekLabel = (
	weekStart: Date,
	today: Date,
	labels: WeekLabels,
): string => {
	const weeks = differenceInCalendarWeeks(
		startOfWeek(today, WEEK_OPTIONS),
		startOfWeek(weekStart, WEEK_OPTIONS),
		WEEK_OPTIONS,
	);
	if (weeks === 0) return labels.thisWeek;
	if (weeks === 1) return labels.lastWeek;
	return labels.weeksAgo(weeks);
};

export const formatMonthLabel = (date: Date): string =>
	capitalizeFirst(format(date, DateFormat.MonthYearLong, { locale: cs }));

const earlierWeekKey = (date: Date): string =>
	`earlier-week-${getISOWeekYear(date)}-W${String(getISOWeek(date)).padStart(2, "0")}`;

const earlierMonthKey = (date: Date): string =>
	`earlier-month-${getYear(date)}-${String(getMonth(date) + 1).padStart(2, "0")}`;

export const classifyRecentDate = (
	itemDate: Date,
	today: Date,
	cutoffEarlierWeek: Date,
): RecentBucket => {
	if (differenceInCalendarDays(today, itemDate) === 0) {
		return { kind: "today", key: "today" };
	}
	if (isSameWeek(itemDate, today, WEEK_OPTIONS)) {
		return { kind: "thisWeek", key: "thisWeek" };
	}
	const itemWeekStart = startOfWeek(itemDate, WEEK_OPTIONS);
	if (
		differenceInCalendarDays(itemWeekStart, cutoffEarlierWeek) >= 0 &&
		differenceInCalendarDays(itemDate, today) < 0
	) {
		return {
			kind: "earlierWeek",
			key: earlierWeekKey(itemDate),
			representativeDate: itemWeekStart,
		};
	}
	return {
		kind: "earlierMonth",
		key: earlierMonthKey(itemDate),
		representativeDate: startOfMonth(itemDate),
	};
};
